import { useState } from "react";
import { auth, db, sendVerificationEmailFn, sendPasswordResetFn } from "../../firebase";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
// Premium form field — matches igo-premium-ui.html .form-input exactly
const PremiumField = ({ label, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 0 }}>
    {label && <label style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", fontFamily: "Syne", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>}
    <input style={{ width: "100%", padding: "13px 16px", background: "var(--paper)", border: "1.5px solid var(--border)", borderRadius: 14, fontSize: 14, color: "var(--ink)", outline: "none", transition: "all .2s" }} {...props}/>
  </div>
);

// ── Auth Screen (handles login, signup, forgot password internally) ────────────
const Login = () => {
  const [mode, setMode]             = useState("login");
  const [form, setForm]             = useState({ name: "", email: "", phone: "", password: "" });
  const [err,  setErr]              = useState("");
  const [busy, setBusy]             = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent,  setResetSent]  = useState(false);
  const [resetMode,  setResetMode]  = useState(false);

  const handleReset = async () => {
    if (!resetEmail) { setErr("Enter your email address."); return; }
    setBusy(true); setErr("");
    try {
      await sendPasswordResetFn({ email: resetEmail });
    } catch (e) {
      // Don't reveal whether the email exists — always show success
      if (e.code !== "auth/user-not-found" && e.code !== "auth/invalid-email") {
        setErr(e.message || "Something went wrong.");
        setBusy(false);
        return;
      }
    }
    setResetSent(true);
    setBusy(false);
  };

  const handle = async () => {
    setErr(""); setBusy(true);
    try {
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, form.email, form.password);
        // onAuthStateChanged checks firebaseUser.emailVerified and routes accordingly
      } else {
        if (!form.name || !form.email || !form.phone || !form.password) { setErr("Please fill all fields."); setBusy(false); return; }
        const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
        const avatar = form.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
        await setDoc(doc(db, "users", cred.user.uid), {
          name: form.name,
          rating: 0, ratingCount: 0, avatar,
          createdAt: serverTimestamp(),
        });
        await setDoc(doc(db, "users", cred.user.uid, "private", "info"), {
          email: form.email, phone: form.phone,
        });
        await sendVerificationEmailFn({ email: cred.user.email });
        // onAuthStateChanged sees emailVerified:false → routes to EmailVerifyPendingScreen
      }
    } catch (e) {
      if (e.code === "auth/email-already-in-use") {
        // Check if it's an unverified account the user abandoned before verifying
        try {
          const existing = await signInWithEmailAndPassword(auth, form.email, form.password);
          if (!existing.user.emailVerified) {
            await sendVerificationEmailFn({ email: existing.user.email });
            setBusy(false);
            return; // onAuthStateChanged routes to EmailVerifyPendingScreen
          }
          await signOut(auth);
        } catch (_) {}
        setErr("An account with this email already exists. Please log in.");
      } else {
        setErr(
          e.code === "auth/wrong-password" || e.code === "auth/user-not-found" || e.code === "auth/invalid-credential" ? "Invalid email or password."
          : e.code === "auth/weak-password" ? "Password must be at least 6 characters."
          : e.message || "Something went wrong."
        );
      }
    }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", background: "var(--paper)" }}>
      {/* ── Premium Header Banner ── */}
      <div style={{ background: "linear-gradient(160deg, #0f1528 0%, #1a2e6e 50%, #2b5be0 100%)", padding: "64px 28px 44px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -50, right: -50, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,.08)", filter: "blur(40px)" }}/>
        <div style={{ position: "absolute", bottom: -30, left: -20, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.06)", filter: "blur(30px)" }}/>
        <div className="apulse" style={{ position: "relative", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", display: "inline-block", boxShadow: "0 0 20px rgba(255,255,255,.4)" }}/>
          <h1 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 40, lineHeight: 1, margin: 0 }}><span style={{ color: "#fff" }}>i</span><span style={{ color: "#5a80f0" }}>Go</span></h1>
        </div>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,.65)", position: "relative" }}>Share rides, save money, connect.</p>
      </div>

      {/* ── Form Area ── */}
      <div className="aslide" style={{ flex: 1, padding: "28px 24px 40px", maxWidth: 430, width: "100%", margin: "0 auto" }}>
        {resetMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <h3 style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 20, color: "var(--ink)", marginBottom: 4 }}>Reset password</h3>
            {resetSent ? (
              <div className="apop" style={{ background: "var(--success-bg)", border: "1.5px solid var(--success-border)", borderRadius: 12, padding: "14px 16px", color: "var(--success)", fontSize: 14, fontWeight: 500 }}>
                Password reset email sent! Check your inbox.
              </div>
            ) : (
              <>
                <PremiumField label="Email" type="email" placeholder="you@email.com" value={resetEmail} onChange={e => setResetEmail(e.target.value)}/>
                {err && <div className="apop" style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, fontWeight: 500 }}>{err}</div>}
                <button onClick={handleReset} disabled={busy} style={{ width: "100%", padding: 14, background: "var(--accent)", color: "#fff", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", boxShadow: "0 4px 16px rgba(43,91,224,.25)", opacity: busy ? 0.5 : 1 }}>
                  {busy ? <span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/> : "Send Reset Email"}
                </button>
              </>
            )}
            <button onClick={() => { setResetMode(false); setResetSent(false); setErr(""); }} style={{ fontSize: 13, color: "var(--muted)", background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}>← Back to Log In</button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <h3 style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 20, color: "var(--ink)", marginBottom: 4 }}>{mode === "login" ? "Welcome back" : "Create account"}</h3>
            {mode === "signup" && <PremiumField label="Full Name" placeholder="Your full name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}/>}
            <PremiumField label="Email" type="email" placeholder="you@email.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}/>
            {mode === "signup" && <PremiumField label="Phone" type="tel" placeholder="+357" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}/>}
            <PremiumField label="Password" type="password" placeholder={mode === "signup" ? "Min 8 characters" : "••••••••"} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}/>
            {mode === "login" && (
              <button onClick={() => { setResetMode(true); setResetEmail(form.email); setErr(""); }} style={{ fontSize: 13, color: "var(--accent)", background: "none", border: "none", cursor: "pointer", padding: "0", textAlign: "left", fontWeight: 600 }}>Forgot password?</button>
            )}
            {err && <div className="apop" style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, fontWeight: 500 }}>{err}</div>}
            <button onClick={handle} disabled={busy} style={{ width: "100%", padding: 14, background: "var(--accent)", color: "#fff", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", boxShadow: "0 4px 16px rgba(43,91,224,.25)", opacity: busy ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {busy ? <span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/> : (mode === "login" ? "Log In" : "Create Account")}
            </button>
            <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
              {mode === "login" ? "No account? " : "Have an account? "}
              <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErr(""); }} style={{ color: "var(--accent)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>
                {mode === "login" ? "Sign up" : "Sign in"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
